import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, UserCircle } from "lucide-react";
import { Avatar } from "./Avatar";
import { Segmented } from "./Segmented";
import { ThemeToggle } from "./ThemeToggle";
import { useT } from "../i18n";

/**
 * Top-bar avatar menu for the AppShell `userMenu` slot (§5): profile link, theme, language, sign out.
 * A real button opens a menu (role="menu"); Escape and outside clicks close it; focus returns to the button.
 */
export function AvatarMenu({ user, onLogout, profileTo, profileLabel, locale, locales, onLocaleChange }: {
  user: { displayName: string; email?: string | null }; onLogout: () => void;
  profileTo?: string; profileLabel?: string;
  locale?: string; locales?: { value: string; label: string }[]; onLocaleChange?: (locale: string) => void;
}) {
  const t = useT();
  const id = useId();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => { if (root.current && !root.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { setOpen(false); btn.current?.focus(); } };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("pointerdown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);
  const label = t("ui.accountMenu", { name: user.displayName });
  return (
    <div ref={root} className="relative">
      <button ref={btn} type="button" className="flex min-h-11 items-center gap-2 rounded-full pr-1 focus-ring" aria-haspopup="menu" aria-expanded={open} aria-controls={id} aria-label={label} onClick={() => setOpen((o) => !o)}>
        <Avatar name={user.displayName} size={36} />
      </button>
      {open && (
        <div id={id} role="menu" aria-label={label} className="absolute right-0 top-full z-40 mt-2 w-72 rounded-panel border border-line bg-surface p-2 shadow-e2">
          <div className="flex items-center gap-2.5 px-2 py-2">
            <Avatar name={user.displayName} size={32} />
            <div className="min-w-0"><div className="truncate font-semibold">{user.displayName}</div>{user.email && <div className="truncate text-small text-fg-3">{user.email}</div>}</div>
          </div>
          {profileTo && <Link role="menuitem" to={profileTo} className="flex min-h-11 items-center gap-2.5 rounded-control px-2 font-medium text-fg no-underline hover:bg-surface-2" onClick={() => setOpen(false)}><UserCircle className="h-4 w-4 text-fg-3" aria-hidden="true" />{profileLabel ?? t("ui.profile")}</Link>}
          <div className="mt-1 flex flex-col gap-1.5 border-t border-line px-2 pb-2 pt-2.5">
            <span className="text-label text-fg-3">{t("ui.theme")}</span>
            <ThemeToggle size="md" />
          </div>
          {locales && onLocaleChange && (
            <div className="flex flex-col gap-1.5 px-2 pb-2">
              <span className="text-label text-fg-3">{t("ui.language")}</span>
              <Segmented<string> ariaLabel={t("ui.language")} variant="pill" size="md" value={locale ?? "en"} onChange={onLocaleChange} options={locales} />
            </div>
          )}
          <button role="menuitem" type="button" className="flex min-h-11 w-full items-center gap-2.5 rounded-control border-t border-line px-2 text-left font-medium text-fg hover:bg-surface-2" onClick={onLogout}><LogOut className="h-4 w-4 text-fg-3" aria-hidden="true" />{t("ui.signOut")}</button>
        </div>
      )}
    </div>
  );
}
